import { useMemo, useState } from 'react';

import { Bar, Empty, Field, StatusBadge } from '../../components/ui/bits.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import { fmtDate, today } from '../../lib/format.js';
import { flightStats, numberFlights } from '../../lib/progress.js';
import { useSola } from '../../sola.jsx';
import * as api from '../../lib/api.js';
import { Icon } from '../../components/ui/Icon.jsx';
import { t } from '../../i18n/index.js';

/*
  Knjižica letenja.

  Popravek proti prejšnji verziji: zaporedna številka leta zdaj šteje od
  najstarejšega. Prej je bil uporabljen indeks v padajoče razvrščenem
  seznamu, zato je bil najnovejši let označen kot „1“ — obratno od knjižice
  na papirju in obratno od PDF izvoza.
*/

export function FlightLog({ uid, profile, flights, onChanged, readOnly = false }) {
  const { toast, confirm } = useUI();
  const [form, setForm] = useState({ flight_date: '', vzletisce: '', opomba: '' });
  const [busy, setBusy] = useState(false);
  // Obrazec je zložen, dokler ga ne odpreš; brez letov je odprt takoj.
  const [open, setOpen] = useState(flights.length === 0);
  const [exporting, setExporting] = useState(false);

  const { pogoji, sola } = useSola();
  const IZPIT_MIN_LETOV = pogoji.min_letov;
  const stats = useMemo(() => flightStats(flights), [flights]);
  const rows = useMemo(() => numberFlights(flights), [flights]);
  // Vzletišča, ki jih je tečajnik že vpisal — za hitrejši vnos naslednjega leta.
  const vzletisca = useMemo(
    () => [...new Set(flights.map((f) => f.vzletisce?.trim()).filter(Boolean))].sort(),
    [flights],
  );

  const add = async (e) => {
    e.preventDefault();
    if (!form.flight_date) return;
    setBusy(true);
    try {
      await api.addFlight(uid, form);
      setForm({ flight_date: '', vzletisce: '', opomba: '' });
      setOpen(false);
      toast.ok(t('Let je vpisan. Viden bo, ko ga inštruktor potrdi.'));
      await onChanged();
    } catch (err) {
      toast.bad(err.message ?? t('Leta ni bilo mogoče vpisati.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (f) => {
    const ok = await confirm({
      title: t('Izbrišem ta let?'),
      body: `${t('Let z dne {d}', { d: fmtDate(f.flight_date) })}${f.vzletisce ? ` (${f.vzletisce})` : ''}.`,
      confirmLabel: t('Izbriši'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deleteFlight(f.id);
      toast.ok(t('Let je izbrisan.'));
      await onChanged();
    } catch (err) {
      toast.bad(err.message ?? t('Brisanje ni uspelo.'));
    }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const { exportFlightLogPDF } = await import('../../lib/pdf.js');
      await exportFlightLogPDF(profile, flights, { minLetov: pogoji.min_letov, sola });
    } catch (err) {
      toast.bad(t('PDF ni bilo mogoče ustvariti.') + ' ' + (err.message ?? ''));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="stack stack--loose">
      <div className="tiles tiles--3">
        <Stat label={t('Potrjeni leti')} value={`${stats.confirmed}`} sub={t('od {n}', { n: IZPIT_MIN_LETOV })} />
        <Stat label={t('Čaka potrditev')} value={`${stats.pending}`} />
        <Stat label={t('Vzletišča')} value={`${stats.sites}`} />
      </div>

      <div className="row">
        <Bar
          done={Math.min(stats.confirmed, IZPIT_MIN_LETOV)}
          total={IZPIT_MIN_LETOV}
          thick
          ok={stats.confirmed >= IZPIT_MIN_LETOV}
          label={t('Potrjenih letov: {a} od {b}', { a: stats.confirmed, b: IZPIT_MIN_LETOV })}
        />
        <strong className="t-md t-brand num">
          {stats.confirmed}/{IZPIT_MIN_LETOV}
        </strong>
      </div>

      {!readOnly && !open && (
        <button className="btn btn--primary btn--lg btn--block" onClick={() => setOpen(true)} aria-expanded="false">
          <Icon name="plus" size={18} /> {t('Vpiši let')}
        </button>
      )}

      {!readOnly && open && (
      <form className="card card--pad stack" onSubmit={add} aria-label={t('Vpiši let')}>
        <div className="row">
          <h3 className="spacer">{t('Vpiši let')}</h3>
          {flights.length > 0 && (
            <button type="button" className="btn-icon btn-icon--sm" onClick={() => setOpen(false)} aria-label={t('Zapri')}>
              <Icon name="x" size={16} />
            </button>
          )}
        </div>
        <div className="grid-2">
          <Field label={t('Datum')} required id="fl-date">
            <input
              id="fl-date"
              type="date"
              className="input"
              max={today()}
              value={form.flight_date}
              onChange={(e) => setForm((f) => ({ ...f, flight_date: e.target.value }))}
            />
          </Field>
          <Field label={t('Vzletišče')} id="fl-site">
            <input
              id="fl-site"
              className="input"
              placeholder={t('npr. Tolmin, Lijak')}
              list="fl-site-list"
              value={form.vzletisce}
              onChange={(e) => setForm((f) => ({ ...f, vzletisce: e.target.value }))}
            />
            <datalist id="fl-site-list">
              {vzletisca.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
          </Field>
        </div>
        <Field label={t('Opomba')} hint={t('Pogoji, padalo, kako je šlo')} id="fl-note">
          <input
            id="fl-note"
            className="input"
            value={form.opomba}
            onChange={(e) => setForm((f) => ({ ...f, opomba: e.target.value }))}
          />
        </Field>
        <button className="btn btn--primary" type="submit" disabled={!form.flight_date || busy}>
          {busy ? t('Vpisujem …') : t('Shrani let')}
        </button>
      </form>
      )}

      <button
        className="btn btn--secondary btn--block"
        onClick={exportPdf}
        disabled={!flights.length || exporting}
      >
        <Icon name="download" size={17} />
        {exporting ? t('Pripravljam PDF …') : t('Izvozi knjižico v PDF')}
      </button>

      {rows.length === 0 ? (
        <div className="card">
          <Empty
            icon="plane"
            title={t('Še ni vpisanih letov')}
            hint={t('Vpiši prvi let zgoraj. Inštruktor ga potrdi, ko ga pregleda.')}
          />
        </div>
      ) : (
        <div className="card list">
          {rows.map((f) => (
            <div key={f.id} className="list__row">
              <span
                className="num"
                aria-hidden="true"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                  fontWeight: 800,
                  fontSize: 13,
                  background: f.status === 'confirmed' ? 'var(--brand)' : 'var(--surface-3)',
                  color: f.status === 'confirmed' ? '#fff' : 'var(--text-3)',
                }}
              >
                {f.seq}
              </span>

              <div className="spacer">
                <div className="t-md t-bold">{fmtDate(f.flight_date)}</div>
                <div className="t-sm t-dim truncate">
                  {f.vzletisce}
                  {f.vzletisce && f.opomba ? ' · ' : ''}
                  {f.opomba && <em>{f.opomba}</em>}
                </div>
              </div>

              <StatusBadge status={f.status} />

              {f.status !== 'confirmed' && !readOnly && (
                <button
                  className="btn-icon btn-icon--sm"
                  onClick={() => remove(f)}
                  aria-label={t('Izbriši let št. {n}', { n: f.seq })}
                >
                  <Icon name="x" size={15} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub }) {
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value">{value}</span>
      {sub && <span className="t-xs t-dimmer num">{sub}</span>}
    </div>
  );
}
